import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const CreateExerciseModal = ({ onClose, onCreate }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'strength',
    difficulty: 'beginner',
    instructions: [''],
    equipmentNeeded: [''],
    muscleGroups: [''],
    caloriesPerMinute: ''
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsLoading(true);

    const exerciseData = {
      ...formData,
      instructions: formData?.instructions?.filter(i => i?.trim()),
      equipmentNeeded: formData?.equipmentNeeded?.filter(e => e?.trim()),
      muscleGroups: formData?.muscleGroups?.filter(m => m?.trim()),
      caloriesPerMinute: parseFloat(formData?.caloriesPerMinute) || null,
      isPublic: true
    };

    await onCreate(exerciseData);
    setIsLoading(false);
  };

  const handleArrayChange = (field, index, value) => {
    const newArray = [...formData?.[field]];
    newArray[index] = value;
    setFormData(prev => ({ ...prev, [field]: newArray }));
  };

  const addArrayItem = (field) => {
    setFormData(prev => ({ ...prev, [field]: [...prev?.[field], ''] }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-6 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-foreground">Create New Exercise</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <Icon name="X" size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <Input
            label="Exercise Name"
            value={formData?.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e?.target?.value }))}
            required
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Description</label>
            <textarea
              value={formData?.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e?.target?.value }))}
              className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Category</label>
              <select
                value={formData?.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e?.target?.value }))}
                className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground"
              >
                <option value="strength">Strength</option>
                <option value="cardio">Cardio</option>
                <option value="flexibility">Flexibility</option>
                <option value="balance">Balance</option>
                <option value="sports">Sports</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Difficulty</label>
              <select
                value={formData?.difficulty}
                onChange={(e) => setFormData(prev => ({ ...prev, difficulty: e?.target?.value }))}
                className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground"
              >
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </div>
          </div>

          <Input
            label="Calories per Minute"
            type="number"
            step="0.1"
            value={formData?.caloriesPerMinute}
            onChange={(e) => setFormData(prev => ({ ...prev, caloriesPerMinute: e?.target?.value }))}
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Instructions</label>
            {formData?.instructions?.map((instruction, idx) => (
              <Input
                key={idx}
                value={instruction}
                onChange={(e) => handleArrayChange('instructions', idx, e?.target?.value)}
                placeholder={`Step ${idx + 1}`}
                className="mb-2"
              />
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => addArrayItem('instructions')}>
              <Icon name="Plus" size={16} className="mr-2" />
              Add Step
            </Button>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Equipment Needed</label>
            {formData?.equipmentNeeded?.map((equipment, idx) => (
              <Input
                key={idx}
                value={equipment}
                onChange={(e) => handleArrayChange('equipmentNeeded', idx, e?.target?.value)}
                placeholder="Equipment"
                className="mb-2"
              />
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => addArrayItem('equipmentNeeded')}>
              <Icon name="Plus" size={16} className="mr-2" />
              Add Equipment
            </Button>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Muscle Groups</label>
            {formData?.muscleGroups?.map((muscle, idx) => (
              <Input
                key={idx}
                value={muscle}
                onChange={(e) => handleArrayChange('muscleGroups', idx, e?.target?.value)}
                placeholder="Muscle group"
                className="mb-2"
              />
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => addArrayItem('muscleGroups')}>
              <Icon name="Plus" size={16} className="mr-2" />
              Add Muscle Group
            </Button>
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="submit" loading={isLoading} fullWidth>
              Create Exercise
            </Button>
            <Button type="button" variant="outline" onClick={onClose} fullWidth>
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateExerciseModal;